import _ from 'lodash'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { useProjectPrivateContext } from '../../context/projectContext'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import Loading from '../../components/process-state-notifications/Loading'
import { DeleteRowConfirmationDialog } from '../../containers/BulkSheetView/components/dialog/DeleteRowConfirmationDialog'
import { intl } from '../../../i18n'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  GridApi,
  GridReadyEvent,
  IRowNode,
  RowDataUpdatedEvent,
} from 'ag-grid-community'
import { useGridContext } from '../../containers/BulkSheetView/hooks/useGridContext'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import {
  getRowNumber,
  getSelectedNode,
} from '../../containers/BulkSheetView/lib/gridApi'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { useDispatch } from 'react-redux'
import { useWbsItemAdditionalPropertyGridOptions } from './gridOptions'
import {
  getName,
  WbsItemAdditionalPropertyRow,
} from './rowModel/wbsItemAdditionalPropertyRow'
import { useDialog } from './hooks/dialog'
import { useWbsItemAdditionalPropertyContextMenu } from './hooks/contextMenu'
import { styled } from '@mui/material'
import ReloadButton from '../../containers/BulkSheetView/components/header/ReloadButton'
import FilterButton from '../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../containers/BulkSheetView/components/header/SortButton'
import { useColumnState } from '../../containers/BulkSheetView/hooks/columnState/useColumnState'
import { useWbsItemAdditionalPropertyRowData } from './hooks/rowData'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import ColumnSettingButton from '../../containers/BulkSheetView/components/header/ColumnSettingButton'
import { useRowDragAndDrop } from './hooks/dragAndDrop'
import { fetchWbsItemAdditionalProperties as fetchWbsItemAdditionalPropertiesAction } from '../../../store/wbsItemAdditionalProperty'
import { doNotRequireSave, requireSave } from '../../../store/requiredSaveData'
import {
  addGlobalMessage,
  addScreenMessage,
  MessageLevel,
} from '../../../store/messages'
import { useWbsItemTypes } from './hooks/wbsItemType'
import {
  InputError,
  validateNodes,
} from '../../containers/BulkSheetView/lib/validation'
import ConfirmDialog, {
  useConfirmDialog,
} from '../WbsItem/components/dialog/ConfirmDialog'

type WrapperProps = PageProps

export const WbsItemAdditionalProperties = projectPrivate(
  ({ uuid: functionUuid }: WrapperProps) => {
    const { projectUuid } = useProjectPrivateContext()
    const [loading, setLoading] = useState<boolean>(true)
    const dialog = useDialog()
    const ref = useRef<HTMLDivElement>(null)
    const { wbsItemTypes } = useWbsItemTypes()
    const gridOptions = useWbsItemAdditionalPropertyGridOptions()
    const gridApiRef = useRef<GridApi>()
    const [isGridReady, setIsGridReady] = useState(false)
    const { contextRef, updateContext } = useGridContext(
      gridApiRef.current,
      gridOptions.context
    )
    const {
      data,
      originalData,
      setDataAfterEditing,
      fetchWbsItemAdditionalProperties,
      saveWbsItemAdditionalProperties,
      addGroupHeaderRow,
      addAdditionalPropertyRow,
      deleteRows,
      hasChanged,
    } = useWbsItemAdditionalPropertyRowData({
      projectUuid,
      wbsItemTypes,
      contextRef,
      gridApi: gridApiRef.current,
    })
    const bulkSheetState = useBulkSheetState(functionUuid, projectUuid)
    const columnState = useColumnState({
      gridApi: gridApiRef.current,
      autoGroupColumnDef: gridOptions.autoGroupColumnDef,
      bulkSheetState,
    })
    const columnSetting = useColumnSetting()
    const dispatch = useDispatch()

    const { onRowDragEnter, onRowDragMove, onRowDragEnd, refreshDragStyle } =
      useRowDragAndDrop(
        ref,
        data,
        setDataAfterEditing,
        gridApiRef.current,
        updateContext
      )

    useEffect(() => {
      updateContext({
        wbsItemType: wbsItemTypes,
      })
    }, [updateContext, wbsItemTypes])
    useEffect(() => {
      updateContext({
        originalData,
      })
    }, [updateContext, originalData])

    const refreshAll = useCallback(async () => {
      setLoading(true)
      try {
        await fetchWbsItemAdditionalProperties()
        dispatch(doNotRequireSave())
      } finally {
        updateContext({
          onTree: false,
          draggableNodeId: undefined,
          errors: new InputError(),
        })
        setLoading(false)
      }
    }, [fetchWbsItemAdditionalProperties, dispatch, updateContext])

    const validateBeforeSubmit = useCallback(
      (rows: WbsItemAdditionalPropertyRow[]) => {
        // Rows that are added but not edited will not be registered.　So ignore them.
        const targetRowUuids = rows
          .filter(row => row.edited)
          .map(row => row.uuid)
        const errors = validateNodes({
          gridApi: gridApiRef.current,
          uuids: targetRowUuids,
          extraValidate: (colId, node, colDef) => {
            if (!node || !colId) return
            const validate = colDef.cellRendererParams?.validate
            if (!validate || !gridApiRef.current) return
            const val = gridApiRef.current.getValue(colId, node)
            return validate(val, {
              node,
              data: node.data,
              colDef,
              colId,
              api: gridApiRef.current,
            })
          },
        })
        updateContext({
          errors,
        })
      },
      [updateContext]
    )
    const onSubmit = useCallback(async () => {
      setLoading(true)
      try {
        gridApiRef.current?.stopEditing()

        validateBeforeSubmit(data.filter(row => row.added || row.edited))
        if (contextRef.current.errors?.hasMessage()) {
          dispatch(
            addGlobalMessage({
              type: MessageLevel.WARN,
              title: intl.formatMessage({ id: 'global.warning.businessError' }),
              text: contextRef.current.errors.toMessage(id => {
                const node = gridApiRef.current?.getRowNode(id)
                return !!node ? getRowNumber(node).toString() : ''
              }),
            })
          )
          return
        }

        await saveWbsItemAdditionalProperties()
        dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
          })
        )
        dispatch(fetchWbsItemAdditionalPropertiesAction(projectUuid))
        dispatch(doNotRequireSave())
        await refreshAll()
      } finally {
        setLoading(false)
      }
    }, [
      projectUuid,
      data,
      saveWbsItemAdditionalProperties,
      dispatch,
      functionUuid,
      refreshAll,
      validateBeforeSubmit,
    ])
    const onReload = useCallback(() => {
      if (hasChanged()) {
        dialog.openCancel()
      } else {
        refreshAll()
      }
    }, [refreshAll, hasChanged])

    const { contextMenu } = useWbsItemAdditionalPropertyContextMenu({
      addGroupHeaderRowAction: addGroupHeaderRow,
      addAdditionalPropertyRowAction: addAdditionalPropertyRow,
      deleteRowAction: dialog.openDeleteConfirmation,
      allRows: data,
    })
    const onGridReady = useCallback(
      (event: GridReadyEvent) => {
        gridApiRef.current = event.api
        setIsGridReady(true)
        if (!_.isEmpty(bulkSheetState.columnState)) {
          event.api.applyColumnState({
            state: bulkSheetState.columnState,
            applyOrder: true,
          })
        }
      },
      [bulkSheetState.columnState]
    )

    // Restore row state.
    const onFirstDataRendered = useCallback(
      e => {
        if (!gridApiRef.current) return

        if (!_.isEmpty(bulkSheetState.filterState)) {
          gridApiRef.current.setFilterModel(bulkSheetState.filterState ?? null)
        }
        bulkSheetState.finishRestoring()
      },
      [bulkSheetState.filterState]
    )

    const onRowDataUpdated = useCallback(
      (e: RowDataUpdatedEvent<WbsItemAdditionalPropertyRow>) =>
        e.api?.redrawRows(),
      []
    )

    const initialFetched = useRef(false)
    useEffect(() => {
      if (!bulkSheetState.initialized || initialFetched.current) {
        return
      }
      refreshAll()
      initialFetched.current = true
    }, [bulkSheetState.initialized, refreshAll])

    const { openConfirmDialog, confirmDialogProps } = useConfirmDialog()
    const onSubmitWithConfirm = useCallback(() => {
      openConfirmDialog({
        title: intl.formatMessage({
          id: 'wbsItemAdditionalProperties.submit.confirmation.title',
        }),
        message: intl.formatMessage({
          id: 'wbsItemAdditionalProperties.submit.confirmation.message',
        }),
        confirm: onSubmit,
      })
    }, [openConfirmDialog, onSubmit])

    useKeyBind(
      [{ key: KEY_SAVE, fn: onSubmitWithConfirm, stopDefaultBehavior: true }],
      [onSubmitWithConfirm]
    )

    return (
      <PageArea>
        <SavePopper loading={loading} onSubmit={onSubmitWithConfirm} />
        <Header>
          <ReloadButton onReload={onReload} />
          <FilterButton
            filteredColumns={columnState.filteredColumns}
            onDeleteFilteredColumn={columnState.onDeleteFilteredColumn}
            resetFilters={columnState.resetFilters}
          />
          <SortButton
            onDeleteSortedColumn={columnState.onDeleteSortedColumn}
            onDeleteSortedAllColumns={columnState.onDeleteSortedAllColumns}
            onChangeSortColumnState={columnState.onChangeSortColumnState}
            sortColumnsState={columnState.sortColumnsState}
          />
          <ColumnSettingButton
            onClick={columnSetting.toggle}
            open={columnSetting.isOpen}
          />
        </Header>
        {bulkSheetState.initialized && (
          <BulkSheetView
            ref={ref}
            gridOptions={gridOptions}
            rowData={data}
            getContextMenuItems={contextMenu}
            // Events and actions
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onColumnVisible={columnState.onColumnVisible}
            onColumnResized={columnState.rememberColumnState}
            onColumnMoved={columnState.rememberColumnState}
            onFilterChanged={columnState.onFilterChanged}
            onSortChanged={columnState.onSortChanged}
            onRowDataUpdated={onRowDataUpdated}
            onRowDragEnter={onRowDragEnter}
            onRowDragMove={onRowDragMove}
            onRowDragEnd={onRowDragEnd}
            onRowDragLeave={refreshDragStyle}
          />
        )}
        <Loading isLoading={loading} elem={ref.current} />
        {dialog.deleteConfirmation && isGridReady && gridApiRef.current && (
          <DeleteRowConfirmationDialog
            target={getDeleteRowLabels(gridApiRef.current)}
            onConfirm={() => {
              deleteRows()
              dialog.closeDeleteConfirmation()
              dispatch(requireSave())
            }}
            onClose={dialog.closeDeleteConfirmation}
          />
        )}
        {dialog.cancel && (
          <CancelConfirmDialog
            open={true}
            onConfirm={() => {
              dialog.closeCancel()
              refreshAll()
            }}
            onClose={dialog.closeCancel}
          />
        )}
        <ColumnSettingPopper
          anchorEl={columnSetting.anchorEl}
          open={columnSetting.isOpen}
          close={columnSetting.close}
          gridApi={gridApiRef.current || undefined}
          height={ref.current?.offsetHeight}
          openSavedUiStateDialog={undefined}
          initializeColumnState={columnState.resetColumnState}
          applicationFunctionUuid={functionUuid}
          uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
          columnState={bulkSheetState.columnState}
          offset={120}
        />
        <ConfirmDialog {...confirmDialogProps} />
      </PageArea>
    )
  }
)

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '47px',
})

const getDeleteRowLabels = (gridApi: GridApi) => {
  return getSelectedNode(gridApi).map(
    (v: IRowNode<WbsItemAdditionalPropertyRow>) => {
      if (!v.data) return ''
      const name = getName(v.data)
      return (
        name ||
        intl.formatMessage({
          id: 'wbsItemAdditionalProperties.dialog.deleteRowsConfirmation.message.propertyName.undefined',
        })
      )
    }
  )
}
